import styled from '@emotion/styled';
import { List, Grid } from '@mui/material';
import { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useFormContext } from 'react-hook-form';
import {
  WsColors,
  ScrollableTabs,
  WsTypography,
  WsTypoVariants,
  WsButton,
  WsEditor,
  ControlledTextInput,
  Item,
  useWsEditor,
} from 'wisestep-base';

import CandidatePlaceholder from '../../../../../features/emailSettings/components/templates/CandidatePlaceholder';
import JobPlaceholder from '../../../../../features/emailSettings/components/templates/JobPlaceholder';
import RecruiterPlaceholder from '../../../../../features/emailSettings/components/templates/RecruiterPlaceholder';
import ErrorLogger from '../../../../../providers/ErrorLogger';

import { EmailBccOrCcInput } from './EmailBccOrCcInput';
import { FormLabel } from './FormLabel';

const StyledList = styled(List)({
  marginLeft: 15,
  maxHeight: '70vh',
  overflow: 'auto',
  '& .MuiListItem-root:hover': {
    backgroundColor: WsColors.TheBlues50,
    '&, & .MuiListItemIcon-root': {
      color: WsColors.TheBlues500,
    },
  },
});

const EmailTextBorder = styled(Item)(({ theme }) => ({
  borderBottom: '1px solid ' + WsColors.TertiaryShades100,
  borderRadius: 0,
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
}));

const scrollList = [
  {
    tabName: 'Candidate',
  },
  {
    tabName: 'Job',
  },
  {
    tabName: 'Recruiter',
  },
];

interface EmailTemplateProps {
  title: string;
  onReset: () => void;
}

export const EmailTemplate = ({ title, onReset }: EmailTemplateProps) => {
  const [tabPanelPage, setTabPanelPage] = useState(0);
  const [currentEditor, setCurrentEditor] = useState<string>('subject');
  const methods = useFormContext();
  const { setValue, watch, getValues } = methods;

  const onFormDiscard = () => {
    onReset();
    editor?.commands.setContent(watch('emailBody'));
  };

  const editor = useWsEditor(
    getValues('emailBody'),
    (htmlBody) => {
      setValue('emailBody', htmlBody ?? '');
    },
    () => {},
    () => setCurrentEditor('emailBody')
  );

  const placeholderAction = (placeholder: string) => {
    if (currentEditor === 'subject') {
      const subjectValue = watch('subject');
      setValue('subject', `${subjectValue} ${placeholder}`);
    } else {
      editor?.commands.insertContent(placeholder);
    }
  };
  const [showBcc, setShowBcc] = useState<boolean>(false);

  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      <Grid display="flex" flexDirection="column" rowGap={2}>
        <WsTypography bold variant={WsTypoVariants.SUB_TITLE}>
          {title}
        </WsTypography>
        <WsTypography variant={WsTypoVariants.BODY1}>Placeholders</WsTypography>
      </Grid>
      <Grid container columnSpacing={2}>
        <Grid item xs={4}>
          <ScrollableTabs
            scrollList={scrollList}
            getTabIndex={(index: number) => setTabPanelPage(index)}
            defaultValue={tabPanelPage}
          />
          <StyledList>
            {tabPanelPage === 0 && <CandidatePlaceholder onClickPlaceholder={placeholderAction} />}
            {tabPanelPage === 1 && <JobPlaceholder onClickPlaceholder={placeholderAction} />}
            {tabPanelPage === 2 && <RecruiterPlaceholder onClickPlaceholder={placeholderAction} />}
          </StyledList>
        </Grid>
        <Grid bgcolor={WsColors.White} item xs={8}>
          <>
            <EmailTextBorder>
              <Grid container alignItems="center">
                <FormLabel label="Subject" />
                <Grid xs={10}>
                  <ControlledTextInput
                    name="subject"
                    type="text"
                    fullWidth
                    variant="standard"
                    onFocus={() => setCurrentEditor('subject')}
                    InputProps={{
                      disableUnderline: true,
                    }}
                  />
                </Grid>
              </Grid>
            </EmailTextBorder>
            <Grid container justifyContent="space-between" display="flex">
              <Grid item xs={8}>
                <EmailTextBorder>
                  <Grid container alignItems="center">
                    <FormLabel label="CC" />
                    <EmailBccOrCcInput name="cc" />
                  </Grid>
                </EmailTextBorder>
              </Grid>
              <Grid item xs={2} pt={2} direction="row-reverse" justifyContent="flex-end">
                <WsButton onClick={() => setShowBcc(!showBcc)} size="small" variant="text">
                  {showBcc ? 'Hide BCC' : 'Show BCC'}
                </WsButton>
              </Grid>
            </Grid>

            {showBcc && (
              <EmailTextBorder>
                <Grid container alignItems="center">
                  <FormLabel label="BCC" />
                  <EmailBccOrCcInput name="bcc" />
                </Grid>
              </EmailTextBorder>
            )}
          </>
          <WsEditor editor={editor} resizable={true} />
        </Grid>
        <Grid container justifyContent="flex-end" columnGap={2.5} pt={2} pr={2}>
          <Grid item>
            <WsButton onClick={onFormDiscard} variant="outlined">
              Reset
            </WsButton>
          </Grid>
          <Grid item>
            <WsButton type="submit">Save</WsButton>
          </Grid>
        </Grid>
      </Grid>
    </ErrorBoundary>
  );
};
